import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {{ rail: HTMLElement, pointerId: number, startX: number, startScrollLeft: number, moved: boolean } | null} */
  #variantRailDrag = null;
  #suppressVariantClick = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    document.addEventListener('pointerdown', this.#handleOutsidePanelClick);
    document.addEventListener('keydown', this.#handlePanelKeydown);
    this.addEventListener('fenza:quick-panel-added', this.#handleFenzaPanelAdded);
    this.addEventListener('pointerdown', this.#handleVariantRailPointerDown);
    this.addEventListener('pointermove', this.#handleVariantRailPointerMove);
    this.addEventListener('pointerup', this.#handleVariantRailPointerEnd);
    this.addEventListener('pointercancel', this.#handleVariantRailPointerEnd);
    this.addEventListener('click', this.#handleVariantRailClickCapture, true);
    this.addEventListener('wheel', this.#handleVariantRailWheel, { passive: false });
    this.querySelector('.fenza-quick-panel__variants')?.addEventListener('scroll', this.#syncVariantScrollbar, {
      passive: true,
    });
    window.addEventListener('resize', this.#syncVariantScrollbar);
    this.#syncVariantScrollbar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    document.removeEventListener('pointerdown', this.#handleOutsidePanelClick);
    document.removeEventListener('keydown', this.#handlePanelKeydown);
    this.removeEventListener('fenza:quick-panel-added', this.#handleFenzaPanelAdded);
    this.removeEventListener('pointerdown', this.#handleVariantRailPointerDown);
    this.removeEventListener('pointermove', this.#handleVariantRailPointerMove);
    this.removeEventListener('pointerup', this.#handleVariantRailPointerEnd);
    this.removeEventListener('pointercancel', this.#handleVariantRailPointerEnd);
    this.removeEventListener('click', this.#handleVariantRailClickCapture, true);
    this.removeEventListener('wheel', this.#handleVariantRailWheel);
    this.querySelector('.fenza-quick-panel__variants')?.removeEventListener('scroll', this.#syncVariantScrollbar);
    window.removeEventListener('resize', this.#syncVariantScrollbar);
  }

  /** @returns {HTMLElement | null} */
  #getFenzaPanel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.fenza-quick-panel'));
  }

  #closeFenzaPanel = () => {
    const panel = this.#getFenzaPanel();
    if (!panel || panel.hidden) return;

    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-panel-open');
    this.removeAttribute('data-panel-closing');
    panel.removeAttribute('data-closing');
    this.querySelector('.fenza-quick-panel-trigger')?.setAttribute('aria-expanded', 'false');
  };

  #handleFenzaPanelAdded = () => {
    const panel = this.#getFenzaPanel();
    if (!panel || panel.hidden || this.hasAttribute('data-panel-closing')) return;

    this.setAttribute('data-panel-closing', '');
    panel.setAttribute('data-closing', '');
    window.setTimeout(this.#closeFenzaPanel, 520);
  };

  #handleVariantRailPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !(event.target instanceof Element)) return;
    const rail = /** @type {HTMLElement | null} */ (event.target.closest('.fenza-quick-panel__variants'));
    if (!rail || rail.scrollWidth <= rail.clientWidth) return;
    this.#variantRailDrag = {
      rail,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: rail.scrollLeft,
      moved: false,
    };
    rail.setPointerCapture(event.pointerId);
  };

  #handleVariantRailPointerMove = (event) => {
    const drag = this.#variantRailDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < 4) return;
    drag.moved = true;
    drag.rail.classList.add('is-dragging');
    drag.rail.scrollLeft = drag.startScrollLeft - distance;
    event.preventDefault();
  };

  #handleVariantRailPointerEnd = (event) => {
    const drag = this.#variantRailDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    this.#suppressVariantClick = drag.moved;
    drag.rail.classList.remove('is-dragging');
    if (drag.rail.hasPointerCapture(event.pointerId)) drag.rail.releasePointerCapture(event.pointerId);
    this.#variantRailDrag = null;
  };

  #handleVariantRailClickCapture = (event) => {
    if (!this.#suppressVariantClick) return;
    this.#suppressVariantClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #handleVariantRailWheel = (event) => {
    if (!(event.target instanceof Element)) return;
    const rail = /** @type {HTMLElement | null} */ (event.target.closest('.fenza-quick-panel__variants'));
    if (!rail || rail.scrollWidth <= rail.clientWidth || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
    rail.scrollLeft += event.deltaY;
    event.preventDefault();
  };

  #syncVariantScrollbar = () => {
    const rail = /** @type {HTMLElement | null} */ (this.querySelector('.fenza-quick-panel__variants'));
    const scrollbar = /** @type {HTMLElement | null} */ (this.querySelector('.fenza-quick-panel__scrollbar'));
    if (!rail || !scrollbar) return;

    const maxScroll = rail.scrollWidth - rail.clientWidth;
    scrollbar.toggleAttribute('data-inactive', maxScroll <= 1);
    if (maxScroll <= 1) {
      scrollbar.style.setProperty('--fenza-scroll-thumb-width', '100%');
      scrollbar.style.setProperty('--fenza-scroll-thumb-offset', '0px');
      return;
    }

    const trackWidth = scrollbar.clientWidth;
    const thumbWidth = Math.max(22, trackWidth * (rail.clientWidth / rail.scrollWidth));
    const thumbOffset = (rail.scrollLeft / maxScroll) * (trackWidth - thumbWidth);
    scrollbar.style.setProperty('--fenza-scroll-thumb-width', `${thumbWidth}px`);
    scrollbar.style.setProperty('--fenza-scroll-thumb-offset', `${thumbOffset}px`);
  };

  #handleOutsidePanelClick = (event) => {
    if (!this.hasAttribute('data-panel-open')) return;
    const path = event.composedPath();
    if (!path.includes(this)) this.#closeFenzaPanel();
  };

  #handlePanelKeydown = (event) => {
    if (event.key === 'Escape') this.#closeFenzaPanel();
  };

  /** Opens the Hermès-style collection-card panel. */
  handleFenzaPanelToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const panel = this.#getFenzaPanel();
    if (!panel) return;
    const willOpen = panel.hidden;

    document.querySelectorAll('quick-add-component[data-panel-open]').forEach((component) => {
      if (component !== this && component instanceof QuickAddComponent) component.#closeFenzaPanel();
    });

    panel.hidden = !willOpen;
    panel.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
    this.toggleAttribute('data-panel-open', willOpen);
    this.querySelector('.fenza-quick-panel-trigger')?.setAttribute('aria-expanded', String(willOpen));

    if (willOpen) requestAnimationFrame(() => panel.querySelector('.fenza-quick-panel__close')?.focus());
    if (willOpen) requestAnimationFrame(this.#syncVariantScrollbar);
  };

  handleFenzaPanelClose = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#closeFenzaPanel();
  };

  /** Updates the compact panel when a colour thumbnail is selected. */
  handleFenzaVariantSelect = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const eventTarget = event.target;
    const target = /** @type {HTMLElement | null} */ (
      eventTarget instanceof Element ? eventTarget.closest('.fenza-quick-panel__variant') : null
    );
    if (!target) return;

    this.querySelectorAll('.fenza-quick-panel__variant').forEach((button) => {
      const selected = button === target;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });

    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.fenza-quick-panel input[name="id"]'));
    if (variantInput && target.dataset.variantId) variantInput.value = target.dataset.variantId;

    const color = this.querySelector('.fenza-quick-panel__color-value');
    const price = this.querySelector('.fenza-quick-panel__price');
    const title = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.fenza-quick-panel__title'));
    const heroImage = /** @type {HTMLImageElement | null} */ (this.querySelector('.fenza-quick-panel__product-image'));
    if (color && target.dataset.color) color.textContent = target.dataset.color;
    if (price && target.dataset.price) price.textContent = target.dataset.price;
    if (title && target.dataset.productTitle) title.textContent = target.dataset.productTitle;
    if (title && target.dataset.productUrl) title.href = target.dataset.productUrl;
    if (heroImage && target.dataset.image) {
      // The initially rendered image has a responsive srcset. Updating only src
      // lets the browser keep choosing the old variant from that srcset.
      heroImage.removeAttribute('srcset');
      heroImage.removeAttribute('sizes');
      heroImage.src = target.dataset.image;
      heroImage.alt = target.dataset.color || '';
      heroImage.animate(
        [
          { opacity: 0.48, transform: 'scale(.985)' },
          { opacity: 1, transform: 'scale(1)' },
        ],
        { duration: 240, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
    }
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
